import { useState } from "react";
import PageHero from "@/components/PageHero";
import Newsletter from "@/components/Newsletter";
import { FadeIn, ChapterLabel } from "@/components/Reveal";
import { CHARACTERS } from "@/data/content";
import useSEO from "@/hooks/useSEO";

const FILTERS = ["All", "The Magic in You", "The Magic in Shoe", "The Places We Carry"];

export default function Characters() {
  useSEO(
    "Meet the Characters: The Boy, The Magic Baseball & More | The Cap Chronicles",
    "Meet the hearts of The Cap Chronicles picture books for ages 4-8: the boy explorer, the Magic Baseball, the little star of The Magic in Shoe, and The Shadow."
  );
  const [filter, setFilter] = useState("All");
  const shown = filter === "All" ? CHARACTERS : CHARACTERS.filter((c) => c.from && c.from.includes(filter));
  return (
    <main>
      <PageHero
        kicker="The Characters"
        title="Small heroes,"
        italic="enormous hearts."
        description="The creator stays behind the curtain, and the characters do the talking. Meet the friends who carry the Chronicles on their shoulders, shells, and wings."
      />

      <div className="mx-auto flex max-w-7xl flex-wrap gap-3 px-6 pb-14 md:px-12" data-testid="character-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
            className={`rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-widest transition-all duration-300 ${
              filter === f ? "bg-forest text-cream" : "border border-charcoal/15 text-charcoal/60 hover:border-forest hover:text-forest"
            }`}
          >
            {f === "All" ? "All Stories" : f}
          </button>
        ))}
      </div>

      <section className="mx-auto max-w-7xl space-y-24 px-6 pb-24 md:px-12 md:pb-32 md:space-y-32" data-testid="characters-list">
        {shown.map((c, i) => (
          <article key={c.id} className={`grid items-center gap-12 lg:grid-cols-2 lg:gap-20`} data-testid={`character-detail-${c.id}`}>
            <FadeIn className={`relative mx-auto w-full max-w-md ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className={`absolute left-1/2 top-1/2 h-[105%] w-[105%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl ${i % 2 === 0 ? "bg-gold/20" : "bg-sky/20"}`} />
              <img src={c.img} alt={`${c.name}, ${c.role}`} className={`relative w-full rounded-[2.5rem] object-cover shadow-[0_40px_80px_-30px_rgba(34,34,34,0.35)] ${i % 2 === 0 ? "rotate-[-2deg]" : "rotate-[2deg]"}`} />
            </FadeIn>
            <div className={i % 2 === 1 ? "lg:order-1" : ""}>
              <ChapterLabel num={String(i + 1).padStart(2, "0")} title={c.role} />
              <FadeIn delay={0.1}>
                <h2 className="font-serif text-4xl tracking-tight md:text-5xl">{c.name}</h2>
                {c.from && (
                  <span className="mt-4 inline-block rounded-full bg-gold/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-charcoal/70" data-testid={`character-from-${c.id}`}>
                    From {c.from}
                  </span>
                )}
                <p className="mt-6 max-w-lg text-base leading-relaxed text-charcoal/70 md:text-lg">{c.long}</p>
                <blockquote className="mt-8 border-l-2 border-gold pl-6 font-serif text-2xl italic leading-snug text-forest">
                  “{c.quote}”
                </blockquote>
              </FadeIn>
            </div>
          </article>
        ))}
      </section>

      <Newsletter />
    </main>
  );
}
